import React, { useEffect } from "react"
import { useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Spinner,
} from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import { addSlideSchema } from "components/Common/validation"
import Switch from "components/Shared/Switch"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import {
  addSlide,
  getSlideDetails,
  updateSlide,
} from "store/actions/bannerAction"
import {
  addOfferSlide,
  getOfferSlideDetails,
  updateOfferSlide,
} from "store/actions/offerBannerAction"
import { SLIDING_BANNER_CONFIG } from "constants/bannerModuleConfig"
import SweatAlert from "components/Shared/SweatAlert"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import Breadcrumbs from "components/Common/Breadcrumb"
import isPermissionEnabled from "common/isPermissionEnabled"
import { getCategories } from "store/actions/categoriesAction"
import Select from "components/Shared/TranslatedSelect"
import { getItemsList, getItemsListDropdown } from "store/actions/itemsAction"
import BackButton from "components/Shared/BackButton"
import { useTranslation } from "react-i18next"
import imageCompression from "browser-image-compression"
import { bannerHeight, bannerWidth, webBannerHeight, webBannerWidth } from "components/Common/ImageSize"
import TextError from "components/Shared/TextError"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import { getAllGiftCardCategoriesWithoutPagination } from "store/actions/giftCardCategoriesAction"
import useCrossModuleFilterPermissions from "hooks/useCrossModuleFilterPermissions"
import SlideAdvancedSettings, {
  MOBILE_ADVANCED_FIELDS,
  WEB_ADVANCED_FIELDS,
} from "./SlideAdvancedSettings"

const isUploadBinary = value => value instanceof File || value instanceof Blob

const getPreviewDisplaySize = (requiredWidth, requiredHeight, maxWidth = 280) => {
  const scale = maxWidth / requiredWidth
  return {
    width: Math.round(requiredWidth * scale),
    height: Math.round(requiredHeight * scale),
  }
}

const SlideImagePreview = ({
  fileValue,
  urlValue,
  imageType,
  alt,
  previewWidth,
  previewHeight,
}) => {
  const [previewSrc, setPreviewSrc] = React.useState(null)

  React.useEffect(() => {
    if (imageType === "URL") {
      setPreviewSrc(urlValue || null)
      return undefined
    }

    if (isUploadBinary(fileValue)) {
      const objectUrl = URL.createObjectURL(fileValue)
      setPreviewSrc(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    }

    if (typeof fileValue === "string" && fileValue.trim()) {
      setPreviewSrc(fileValue)
      return undefined
    }

    setPreviewSrc(urlValue || null)
    return undefined
  }, [fileValue, urlValue, imageType])

  if (!previewSrc || !previewWidth || !previewHeight) return null

  const { width, height } = getPreviewDisplaySize(previewWidth, previewHeight)

  return (
    <div className="mt-2" style={{ width, height, lineHeight: 0 }}>
      <img
        src={previewSrc}
        alt={alt}
        style={{
          width,
          height,
          display: "block",
          objectFit: "fill",
          border: "1px solid #dee2e6",
          borderRadius: 4,
        }}
      />
    </div>
  )
}

const initialValues = {
  imageType: "UPLOAD",
  imageURL: "",
  imageFile: null,
  applySameImageOnBothLang: true,
  imageUrlTypeAr: "UPLOAD",
  imageUrlAr: "",
  imageAr: "",
  webImageType: "UPLOAD",
  webImageURL: "",
  webImageFile: null,
  applySameWebImageOnBothLang: true,
  webImageUrlTypeAr: "UPLOAD",
  webImageUrlAr: "",
  webImageAr: "",
  hasAdvancedSettings: false,
  title: "",
  titleAr: "",
  description: "",
  descriptionAr: "",
  linkLabel: "",
  linkLabelAr: "",
  linkValue: "",
  linkType: "",
  externalLink: "",
  category: "",
  item: "",
  label: "",
  duration: 0,
  hasWebAdvancedSettings: false,
  webTitle: "",
  webTitleAr: "",
  webDescription: "",
  webDescriptionAr: "",
  webLinkLabel: "",
  webLinkLabelAr: "",
  webLinkValue: "",
  webLinkType: "",
  webExternalLink: "",
  webCategory: "",
  webItem: "",
  webLabel: "",
  webDuration: 0,
  webDisplayOrder: 0,
  displayOrder: 0,
}

const AddSlide = ({ mode, moduleConfig = SLIDING_BANNER_CONFIG }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const isOfferModule = moduleConfig.reduxNamespace === "OfferBanners"
  const slideApi = isOfferModule
    ? {
        getDetails: getOfferSlideDetails,
        add: addOfferSlide,
        update: updateOfferSlide,
      }
    : {
        getDetails: getSlideDetails,
        add: addSlide,
        update: updateSlide,
      }

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Slide "
  )}`

  const { bannerId } = useParams()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const countryCode = searchParams.get("countryCode")
  const [formValues, setFormValues] = useState(initialValues)
  const { allCategories } = useSelector(state => state.Categories)
  const [compressingFields, setCompressingFields] = useState({})
  const { categories: giftCardCategories } = useSelector(
    state => state.GiftCardCategories
  )

  const {
    loading,
    error,
    isLoading,
    isUpdating,
    message,
    actionType,
    slideDetails,
  } = useSelector(state =>
    isOfferModule ? state.OfferBanners : state.Banners
  )

  const { itemsLists } = useSelector(state => state.Items)
  const { itemsListDropdown } = useSelector(state => state.Items)
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const { canFilterByMenus } = useCrossModuleFilterPermissions()

  // Gift Card List link type requires Gift Cards module/permission from the tenant plan.
  const canUseGiftCardListLink = isPermissionEnabled(
    userRole,
    37,
    "gift_card_1",
    accountType,
    accountScope
  )

  useEffect(() => {
    if (canFilterByMenus) {
      getCategoriesList()
    }
    if (canUseGiftCardListLink) {
      dispatch(getAllGiftCardCategoriesWithoutPagination())
    }
  }, [canUseGiftCardListLink, canFilterByMenus])

  const getCategoriesList = () => {
    if (!canFilterByMenus) return
    dispatch(getCategories({ isCombo: false, countryCode }))
  }

  const getItems = categoryId => {
    if (!canFilterByMenus) return
    const payload = {
      perPage: 100,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
      category: [categoryId],
    }
    dispatch(getItemsList(payload))
  }
  const allItemsList = value => {
    if (!canFilterByMenus) return
    const payload = {
      category: value ? [value] : [],
    }
    dispatch(getItemsListDropdown(payload))
  }

  useEffect(() => {
    const { permissionCategoryId, permissions } = moduleConfig
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      permissionCategoryId,
      permissions.add,
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      permissionCategoryId,
      permissions.update
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  // IF HAVE MODE===EDIT THEN UPDATE INITAIL VALUES
  useEffect(() => {
    if (mode === "edit") {
      getOldValues()
    }
  }, [])

  const getOldValues = () => {
    const payload = {
      id,
    }
    dispatch(slideApi.getDetails(payload))
  }

  useEffect(() => {
    if (slideDetails && !loading && mode === "edit") {
      const {
        imageUrlType,
        imageUrl,
        applySameImageOnBothLang,
        imageUrlTypeAr,
        imageUrlAr,
        webImageUrlType,
        webImageUrl,
        applySameWebImageOnBothLang,
        webImageUrlTypeAr,
        webImageUrlAr,
        displayOrder,
        title,
        titleAr,
        description,
        descriptionAr,
        linkLabel,
        linkLabelAr,
        linkType,
        linkValue,
        hasAdvancedSettings,
        duration,
        categoryId,
        hasWebAdvancedSettings,
        webTitle,
        webTitleAr,
        webDescription,
        webDescriptionAr,
        webLinkLabel,
        webLinkLabelAr,
        webLinkType,
        webLinkValue,
        webDuration,
        webDisplayOrder,
        webCategoryId,
        webLabel,
        label,
      } = slideDetails

      const sanitizeLinkType = value =>
        value === "GIFT_CARDS_LIST_VIEW" && !canUseGiftCardListLink
          ? ""
          : value || ""

      const sanitizedLinkType = sanitizeLinkType(linkType)
      const sanitizedWebLinkType = sanitizeLinkType(webLinkType)

      setFormValues({
        ...formValues,
        imageType: imageUrlType,
        imageURL: imageUrl,
        imageFile: imageUrl,
        applySameImageOnBothLang: applySameImageOnBothLang,
        imageUrlTypeAr,
        imageUrlAr,
        imageAr: imageUrlAr || "",
        webImageType: webImageUrlType || "UPLOAD",
        webImageURL: webImageUrl || "",
        webImageFile: webImageUrl || null,
        applySameWebImageOnBothLang:
          applySameWebImageOnBothLang !== undefined
            ? applySameWebImageOnBothLang
            : true,
        webImageUrlTypeAr: webImageUrlTypeAr || "UPLOAD",
        webImageUrlAr: webImageUrlAr || "",
        webImageAr: webImageUrlAr || "",
        hasAdvancedSettings: !!hasAdvancedSettings,
        title: title || "",
        titleAr: titleAr || "",
        description: description || "",
        descriptionAr: descriptionAr || "",
        linkLabel: linkLabel || "",
        linkLabelAr: linkLabelAr || "",
        linkValue:
          sanitizedLinkType === "GIFT_CARDS_LIST_VIEW" ? linkValue || "" : "",
        linkType: sanitizedLinkType,
        label: label || "",
        duration: Number(duration) || 0,
        category: categoryId || "",
        item: sanitizedLinkType === "ITEM" ? linkValue || "" : "",
        externalLink:
          sanitizedLinkType === "EXTERNAL_LINK" ? linkValue || "" : "",
        hasWebAdvancedSettings: !!hasWebAdvancedSettings,
        webTitle: webTitle || "",
        webTitleAr: webTitleAr || "",
        webDescription: webDescription || "",
        webDescriptionAr: webDescriptionAr || "",
        webLinkLabel: webLinkLabel || "",
        webLinkLabelAr: webLinkLabelAr || "",
        webLinkValue:
          sanitizedWebLinkType === "GIFT_CARDS_LIST_VIEW"
            ? webLinkValue || ""
            : "",
        webLinkType: sanitizedWebLinkType,
        webLabel: webLabel || "",
        webDuration: Number(webDuration) || 0,
        webDisplayOrder: Number(webDisplayOrder) || 0,
        webCategory: webCategoryId || "",
        webItem: sanitizedWebLinkType === "ITEM" ? webLinkValue || "" : "",
        webExternalLink:
          sanitizedWebLinkType === "EXTERNAL_LINK" ? webLinkValue || "" : "",
        displayOrder: displayOrder || 0,
      })
      // CALL ITEMS API ONLY IF HAVE LINKTYPE = ITEM
      if (sanitizedLinkType === "ITEM" && categoryId) {
        allItemsList(categoryId)
      } else if (sanitizedWebLinkType === "ITEM" && webCategoryId) {
        allItemsList(webCategoryId)
      }
    }
  }, [slideDetails, canUseGiftCardListLink])

  const categoriesOptions = allCategories?.map(category => ({
    label: category.name,
    value: category._id,
  }))
  // console.log(allCategories)

  const handleOnSubmit = values => {
    const {
      imageType,
      imageURL,
      imageFile,
      applySameImageOnBothLang,
      imageUrlTypeAr,
      imageUrlAr,
      webImageType,
      webImageURL,
      webImageFile,
      applySameWebImageOnBothLang,
      webImageUrlTypeAr,
      webImageUrlAr,
      hasAdvancedSettings,
      title,
      titleAr,
      description,
      descriptionAr,
      linkLabel,
      linkLabelAr,
      linkValue,
      linkType,
      externalLink,
      imageAr,
      webImageAr,
      label,
      duration,
      displayOrder,
      category,
      item,
      hasWebAdvancedSettings,
      webTitle,
      webTitleAr,
      webDescription,
      webDescriptionAr,
      webLinkLabel,
      webLinkLabelAr,
      webLinkValue,
      webLinkType,
      webExternalLink,
      webCategory,
      webItem,
      webLabel,
      webDuration,
      webDisplayOrder,
    } = values

    const resolveLinkType = value =>
      value === "GIFT_CARDS_LIST_VIEW" && !canUseGiftCardListLink ? "" : value

    const resolveLinkValue = (type, categoryValue, itemValue, giftValue, external) => {
      if (type === "CATEGORY") return categoryValue
      if (type === "ITEM") return itemValue
      if (type === "GIFT_CARDS_LIST_VIEW") return giftValue
      if (type === "EXTERNAL_LINK") return external
      return ""
    }

    const resolvedLinkType = resolveLinkType(linkType)
    const resolvedWebLinkType = resolveLinkType(webLinkType)
    const newLinkValues = resolveLinkValue(
      resolvedLinkType,
      category,
      item,
      linkValue,
      externalLink
    )
    const newWebLinkValues = resolveLinkValue(
      resolvedWebLinkType,
      webCategory,
      webItem,
      webLinkValue,
      webExternalLink
    )

    let data = new FormData()
    data.append("imageUrlType", imageType)
    data.append("applySameImageOnBothLang", applySameImageOnBothLang)
    data.append(
      "imageUrlTypeAr",
      applySameImageOnBothLang ? "" : imageUrlTypeAr
    )
    data.append(
      "imageUrlAr",
      typeof imageAr === "string" && imageAr ? imageAr : imageUrlAr || ""
    )
    data.append("webImageUrlType", webImageType)
    data.append("applySameWebImageOnBothLang", applySameWebImageOnBothLang)
    data.append(
      "webImageUrlTypeAr",
      applySameWebImageOnBothLang ? "" : webImageUrlTypeAr
    )
    data.append(
      "webImageUrlAr",
      typeof webImageAr === "string" && webImageAr
        ? webImageAr
        : webImageUrlAr || ""
    )
    data.append("hasAdvancedSettings", hasAdvancedSettings)
    data.append("title", title || "")
    data.append("titleAr", titleAr || "")
    data.append("description", description || "")
    data.append("descriptionAr", descriptionAr || "")
    data.append("linkLabel", linkLabel || "")
    data.append("linkLabelAr", linkLabelAr || "")
    data.append("linkValue", newLinkValues || "")
    data.append("linkType", resolvedLinkType || "")
    data.append("duration", Number(duration) || 0)
    data.append("hasWebAdvancedSettings", !!hasWebAdvancedSettings)
    data.append("webTitle", webTitle || "")
    data.append("webTitleAr", webTitleAr || "")
    data.append("webDescription", webDescription || "")
    data.append("webDescriptionAr", webDescriptionAr || "")
    data.append("webLinkLabel", webLinkLabel || "")
    data.append("webLinkLabelAr", webLinkLabelAr || "")
    data.append("webLinkValue", newWebLinkValues || "")
    data.append("webLinkType", resolvedWebLinkType || "")
    data.append("webDuration", Number(webDuration) || 0)
    data.append("webLabel", webLabel || "")
    data.append("webDisplayOrder", Number(webDisplayOrder) || 0)

    const resolvedMobileImageUrl =
      typeof imageFile === "string" && imageFile
        ? imageFile
        : imageURL || ""
    data.append("imageUrl", resolvedMobileImageUrl)
    if (isUploadBinary(imageFile)) {
      data.append("image", imageFile)
    }
    if (isUploadBinary(imageAr)) {
      data.append("imageAr", imageAr)
    }

    const resolvedWebImageUrl =
      typeof webImageFile === "string" && webImageFile
        ? webImageFile
        : webImageURL || ""
    data.append("webImageUrl", resolvedWebImageUrl)
    if (isUploadBinary(webImageFile)) {
      data.append("webImage", webImageFile)
    }
    if (isUploadBinary(webImageAr)) {
      data.append("webImageAr", webImageAr)
    }

    data.append("label", label || "")
    data.append("displayOrder", displayOrder)
    data.append("bannerGroup", bannerId)
    category && data.append("categoryId", category)
    webCategory && data.append("webCategoryId", webCategory)

    if (mode === "add") {
      dispatch(slideApi.add(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(moduleConfig.basePath)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      data.append("id", id)
      dispatch(slideApi.update(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(moduleConfig.basePath)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  const validateImageDimensions = (file, width, height, callback) => {
    const reader = new FileReader()
    reader.onload = e => {
      const img = new Image()
      img.onload = () => {
        callback(img.width === width && img.height === height)
      }
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  }

  const handleFileChange = async (
    event,
    setFieldValue,
    fileName,
    requiredWidth = bannerWidth,
    requiredHeight = bannerHeight
  ) => {
    const file = event.currentTarget.files[0]
    if (file) {
      validateImageDimensions(
        file,
        requiredWidth,
        requiredHeight,
        async isValid => {
          if (isValid) {
            setFieldValue(fileName, file)
            setCompressingFields(prev => ({ ...prev, [fileName]: true }))

            try {
              // Compression options
              const options = {
                maxSizeMB: 0.5,
                maxWidthOrHeight: Math.max(requiredWidth, requiredHeight, 1024),
                useWebWorker: true,
              }

              // Compress the image file
              const compressedFile = await imageCompression(file, options)

              // Set the compressed file value
              setFieldValue(fileName, compressedFile)
            } catch (error) {
              console.error("File compression failed:", error)
              setFieldValue(fileName, file) // Use original file if compression fails
            } finally {
              setCompressingFields(prev => ({ ...prev, [fileName]: false }))
            }
          } else {
            dispatch(
              showAlert({
                text: `Image dimensions must be ${requiredWidth}x${requiredHeight} pixels.`,
                type: "error",
              })
            )
            event.target.value = ""
            setFieldValue(fileName, null)
          }
        }
      )
    }
  }

  const isAnyImageCompressing = Object.values(compressingFields).some(Boolean)

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add Slide") : t("Edit Slide")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addSlideSchema()}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, errors, touched, setFieldValue }) => (
                    <Form className="banner-slide-form">
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="form_field mb-2">
                            <label className="fw-semibold mb-0">
                              {t("Mobile Slide")}
                            </label>
                          </div>
                        </div>

                        {values.applySameImageOnBothLang ? (
                          <>
                            <div className="col-lg-6">
                              <div className="form_field">
                                <label htmlFor="">{t("Image Type")}</label>
                                <Field
                                  as="select"
                                  id="imageType"
                                  name="imageType"
                                  onChange={e => {
                                    const selectedValue = e.target.value
                                    setFieldValue("imageType", selectedValue)
                                    // Reset the value of imageURL and imageFile when the image type changes
                                    setFieldValue("imageURL", "")
                                    setFieldValue("imageFile", null)
                                  }}
                                >
                                  <option value="URL">{t("Image Url")}</option>
                                  <option value="UPLOAD">
                                    {t("Upload Image")}
                                  </option>
                                </Field>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="form_field">
                                {/* IMAGE URL */}
                                {values.imageType === "URL" && (
                                  <div className="form_field">
                                    <label htmlFor="imageURL">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Image URL")}
                                    </label>
                                    <Field
                                      type="text"
                                      id="imageURL"
                                      name="imageURL"
                                      className={`form-control ${
                                        touched.imageURL && errors.imageURL
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                    />
                                    <TextError name={"imageURL"} />
                                    <SlideImagePreview
                                      fileValue={values.imageFile}
                                      urlValue={values.imageURL}
                                      imageType="URL"
                                      previewWidth={bannerWidth}
                                      previewHeight={bannerHeight}
                                      alt={t("Mobile image preview")}
                                    />
                                  </div>
                                )}
                                {/* UPLOAD IMAGE */}
                                {values.imageType === "UPLOAD" && (
                                  <div className="form_field position-relative">
                                    <label htmlFor="imageFile">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Upload Image")}
                                    </label>
                                    <TranslatedFileInput
                                      id="imageFile"
                                      name="imageFile"
                                      accept="image/jpeg, image/jpg, image/png, image/webp"
                                      invalid={
                                        !!(touched.imageFile && errors.imageFile)
                                      }
                                      onChange={e =>
                                        handleFileChange(
                                          e,
                                          setFieldValue,
                                          "imageFile"
                                        )
                                      }
                                      // onChange={e => {
                                      //   setFieldValue(
                                      //     "imageFile",
                                      //     e.currentTarget.files[0]
                                      //   )
                                      //   // Read uploaded file and set it as image URL for preview
                                      //   const reader = new FileReader()
                                      //   reader.onload = function (event) {
                                      //     setFieldValue(
                                      //       "imageURL",
                                      //       event.target.result
                                      //     )
                                      //   }
                                      //   reader.readAsDataURL(
                                      //     e.currentTarget.files[0]
                                      //   )
                                      // }}
                                    />
                                    {compressingFields.imageFile && (
                                      <Spinner
                                        color="primary"
                                        className="file_upload_btn"
                                      />
                                    )}
                                    <TextError name={"imageFile"} />
                                    <SlideImagePreview
                                      fileValue={values.imageFile}
                                      urlValue={values.imageURL}
                                      imageType={values.imageType}
                                      previewWidth={bannerWidth}
                                      previewHeight={bannerHeight}
                                      alt={t("Mobile image preview")}
                                    />
                                  </div>
                                )}
                                <span>
                                  {t("Recommended size: {{dimension}}", {
                                    dimension: `${bannerWidth}x${bannerHeight}`,
                                  })}
                                </span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="col-lg-6">
                              <div className="form_field">
                                <label htmlFor="">
                                  {t("Image Type(عربي)")}
                                </label>
                                <Field
                                  as="select"
                                  id="imageUrlTypeAr"
                                  name="imageUrlTypeAr"
                                  onChange={e => {
                                    const selectedValue = e.target.value
                                    setFieldValue(
                                      "imageUrlTypeAr",
                                      selectedValue
                                    )
                                    // Reset the value of imageURL and imageFile when the image type changes
                                    setFieldValue("imageUrlAr", "")
                                    setFieldValue("imageAr", null)
                                  }}
                                >
                                  <option value="URL">{t("Image Url")}</option>
                                  <option value="UPLOAD">
                                    {t("Upload Image")}
                                  </option>
                                </Field>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="form_field">
                                {/* IMAGE URL */}
                                {values.imageUrlTypeAr === "URL" && (
                                  <div className="form_field">
                                    <label htmlFor="imageUrlAr">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Image URL(عربي)")}
                                    </label>
                                    <Field
                                      type="text"
                                      id="imageUrlAr"
                                      name="imageUrlAr"
                                    />
                                    <TextError name={"imageUrlAr"} />
                                  </div>
                                )}
                                {/* UPLOAD IMAGE */}
                                {values.imageUrlTypeAr === "UPLOAD" && (
                                  <div className="form_field position-relative">
                                    <label htmlFor="imageFile">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Upload Image(عربي)")}
                                    </label>
                                    <TranslatedFileInput
                                      id="imageAr"
                                      name="imageAr"
                                      accept="image/jpeg, image/jpg, image/png, image/webp"
                                      invalid={
                                        !!(touched.imageAr && errors.imageAr)
                                      }
                                      onChange={e =>
                                        handleFileChange(
                                          e,
                                          setFieldValue,
                                          "imageAr"
                                        )
                                      }
                                      // onChange={e => {
                                      //   setFieldValue(
                                      //     "imageAr",
                                      //     e.currentTarget.files[0]
                                      //   )
                                      //   // Read uploaded file and set it as image URL for preview
                                      //   const reader = new FileReader()
                                      //   reader.onload = function (event) {
                                      //     setFieldValue(
                                      //       "imageAr",
                                      //       event.target.result
                                      //     )
                                      //   }
                                      //   reader.readAsDataURL(
                                      //     e.currentTarget.files[0]
                                      //   )
                                      // }}
                                    />
                                    {compressingFields.imageAr && (
                                      <Spinner
                                        color="primary"
                                        className="file_upload_btn"
                                      />
                                    )}
                                    <TextError name={"imageAr"} />
                                    <SlideImagePreview
                                      fileValue={values.imageAr}
                                      urlValue={values.imageUrlAr}
                                      imageType={values.imageUrlTypeAr}
                                      previewWidth={bannerWidth}
                                      previewHeight={bannerHeight}
                                      alt={t("Mobile image preview")}
                                    />
                                  </div>
                                )}
                                <span>
                                  {t("Recommended size: {{dimension}}", {
                                    dimension: "1200x860",
                                  })}
                                </span>
                              </div>
                            </div>

                            <div className="col-lg-6">
                              <div className="form_field">
                                <label htmlFor="">
                                  {t("Image Type(English)")}
                                </label>
                                <Field
                                  as="select"
                                  id="imageType"
                                  name="imageType"
                                  onChange={e => {
                                    const selectedValue = e.target.value
                                    setFieldValue("imageType", selectedValue)
                                    // Reset the value of imageURL and imageFile when the image type changes
                                    setFieldValue("imageURL", "")
                                    setFieldValue("imageFile", null)
                                  }}
                                >
                                  <option value="URL">{t("Image Url")}</option>
                                  <option value="UPLOAD">
                                    {t("Upload Image")}
                                  </option>
                                </Field>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="form_field">
                                {/* IMAGE URL */}
                                {values.imageType === "URL" && (
                                  <div className="form_field">
                                    <label htmlFor="imageURL">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Image URL(English)")}
                                    </label>
                                    <Field
                                      type="text"
                                      id="imageURL"
                                      name="imageURL"
                                    />
                                    <TextError name={"imageURL"} />
                                    <SlideImagePreview
                                      fileValue={values.imageFile}
                                      urlValue={values.imageURL}
                                      imageType="URL"
                                      previewWidth={bannerWidth}
                                      previewHeight={bannerHeight}
                                      alt={t("Mobile image preview")}
                                    />
                                  </div>
                                )}
                                {/* UPLOAD IMAGE */}
                                {values.imageType === "UPLOAD" && (
                                  <div className="form_field position-relative">
                                    <label htmlFor="imageFile">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Upload Image(English)")}
                                    </label>
                                    <TranslatedFileInput
                                      id="imageFile"
                                      name="imageFile"
                                      accept="image/jpeg, image/jpg, image/png, image/webp"
                                      invalid={
                                        !!(touched.imageFile && errors.imageFile)
                                      }
                                      onChange={e =>
                                        handleFileChange(
                                          e,
                                          setFieldValue,
                                          "imageFile"
                                        )
                                      }
                                      // onChange={e => {
                                      //   setFieldValue(
                                      //     "imageFile",
                                      //     e.currentTarget.files[0]
                                      //   )
                                      //   // Read uploaded file and set it as image URL for preview
                                      //   const reader = new FileReader()
                                      //   reader.onload = function (event) {
                                      //     setFieldValue(
                                      //       "imageURL",
                                      //       event.target.result
                                      //     )
                                      //   }
                                      //   reader.readAsDataURL(
                                      //     e.currentTarget.files[0]
                                      //   )
                                      // }}
                                    />
                                    {compressingFields.imageFile && (
                                      <Spinner
                                        color="primary"
                                        className="file_upload_btn"
                                      />
                                    )}
                                    <TextError name={"imageFile"} />
                                    <SlideImagePreview
                                      fileValue={values.imageFile}
                                      urlValue={values.imageURL}
                                      imageType={values.imageType}
                                      previewWidth={bannerWidth}
                                      previewHeight={bannerHeight}
                                      alt={t("Mobile image preview")}
                                    />
                                  </div>
                                )}
                                <span>
                                  {t("Recommended size: {{dimension}}", {
                                    dimension: "1200x860",
                                  })}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                        {/* <div className="col-lg-6">
                          {values.imageFile && (
                            <img
                              src={values.imageURL}
                              alt="Preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "contain",
                              }}
                            />
                          )}
                        </div> */}
                        {/* <div className="col-lg-6">
                          {values.imageAr && (
                            <img
                              src={values.imageUrlAr}
                              alt="Preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "contain",
                              }}
                            />
                          )}
                        </div> */}

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="">
                              {t("Apply the same images to both language")}
                            </label>
                            <Switch
                              isChecked={values.applySameImageOnBothLang}
                              onClickHandler={() => {
                                setFieldValue(
                                  "applySameImageOnBothLang",
                                  !values.applySameImageOnBothLang
                                )
                              }}
                            />
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="form_field">
                            <label
                              htmlFor=""
                              className="cursor-pointer"
                              onClick={() =>
                                setFieldValue(
                                  "hasAdvancedSettings",
                                  !values.hasAdvancedSettings
                                )
                              }
                            >
                              {t("Mobile Advance Settings")}{" "}
                              <i
                                className={`bx bxs-${
                                  values.hasAdvancedSettings ? "up" : "down"
                                }-arrow`}
                              ></i>
                            </label>
                          </div>
                        </div>

                        {values.hasAdvancedSettings && (
                          <SlideAdvancedSettings
                            fields={MOBILE_ADVANCED_FIELDS}
                            values={values}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            canUseGiftCardListLink={canUseGiftCardListLink}
                            giftCardCategories={giftCardCategories}
                            allCategories={allCategories}
                            itemsListDropdown={itemsListDropdown}
                            allItemsList={allItemsList}
                            language={language}
                          />
                        )}

                        <div className="col-lg-12">
                          <hr className="my-4" />
                          <div className="form_field mb-2">
                            <label className="fw-semibold mb-0">
                              {t("Web Slide")}
                            </label>
                          </div>
                        </div>

                        {values.applySameWebImageOnBothLang ? (
                          <>
                            <div className="col-lg-6">
                              <div className="form_field">
                                <label htmlFor="">
                                  {t("Web Image Type")}
                                </label>
                                <Field
                                  as="select"
                                  id="webImageType"
                                  name="webImageType"
                                  onChange={e => {
                                    const selectedValue = e.target.value
                                    setFieldValue("webImageType", selectedValue)
                                    setFieldValue("webImageURL", "")
                                    setFieldValue("webImageFile", null)
                                  }}
                                >
                                  <option value="URL">{t("Image Url")}</option>
                                  <option value="UPLOAD">
                                    {t("Upload Image")}
                                  </option>
                                </Field>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="form_field">
                                {values.webImageType === "URL" && (
                                  <div className="form_field">
                                    <label htmlFor="webImageURL">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Web Image URL")}
                                    </label>
                                    <Field
                                      type="text"
                                      id="webImageURL"
                                      name="webImageURL"
                                      className={`form-control ${
                                        touched.webImageURL &&
                                        errors.webImageURL
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                    />
                                    <TextError name={"webImageURL"} />
                                    <SlideImagePreview
                                      fileValue={values.webImageFile}
                                      urlValue={values.webImageURL}
                                      imageType="URL"
                                      previewWidth={webBannerWidth}
                                      previewHeight={webBannerHeight}
                                      alt={t("Web image preview")}
                                    />
                                  </div>
                                )}
                                {values.webImageType === "UPLOAD" && (
                                  <div className="form_field position-relative">
                                    <label htmlFor="webImageFile">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Upload Web Image")}
                                    </label>
                                    <TranslatedFileInput
                                      id="webImageFile"
                                      name="webImageFile"
                                      accept="image/jpeg, image/jpg, image/png, image/webp"
                                      invalid={
                                        !!(
                                          touched.webImageFile &&
                                          errors.webImageFile
                                        )
                                      }
                                      onChange={e =>
                                        handleFileChange(
                                          e,
                                          setFieldValue,
                                          "webImageFile",
                                          webBannerWidth,
                                          webBannerHeight
                                        )
                                      }
                                    />
                                    {compressingFields.webImageFile && (
                                      <Spinner
                                        color="primary"
                                        className="file_upload_btn"
                                      />
                                    )}
                                    <TextError name={"webImageFile"} />
                                    <SlideImagePreview
                                      fileValue={values.webImageFile}
                                      urlValue={values.webImageURL}
                                      imageType={values.webImageType}
                                      previewWidth={webBannerWidth}
                                      previewHeight={webBannerHeight}
                                      alt={t("Web image preview")}
                                    />
                                  </div>
                                )}
                                <span>
                                  {t("Recommended size: {{dimension}}", {
                                    dimension: `${webBannerWidth}x${webBannerHeight}`,
                                  })}
                                </span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="col-lg-6">
                              <div className="form_field">
                                <label htmlFor="">
                                  {t("Web Image Type(عربي)")}
                                </label>
                                <Field
                                  as="select"
                                  id="webImageUrlTypeAr"
                                  name="webImageUrlTypeAr"
                                  onChange={e => {
                                    const selectedValue = e.target.value
                                    setFieldValue(
                                      "webImageUrlTypeAr",
                                      selectedValue
                                    )
                                    setFieldValue("webImageUrlAr", "")
                                    setFieldValue("webImageAr", null)
                                  }}
                                >
                                  <option value="URL">{t("Image Url")}</option>
                                  <option value="UPLOAD">
                                    {t("Upload Image")}
                                  </option>
                                </Field>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="form_field">
                                {values.webImageUrlTypeAr === "URL" && (
                                  <div className="form_field">
                                    <label htmlFor="webImageUrlAr">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Web Image URL(عربي)")}
                                    </label>
                                    <Field
                                      type="text"
                                      id="webImageUrlAr"
                                      name="webImageUrlAr"
                                      className={`form-control ${
                                        touched.webImageUrlAr &&
                                        errors.webImageUrlAr
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                    />
                                    <TextError name={"webImageUrlAr"} />
                                  </div>
                                )}
                                {values.webImageUrlTypeAr === "UPLOAD" && (
                                  <div className="form_field position-relative">
                                    <label htmlFor="webImageAr">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Upload Web Image(عربي)")}
                                    </label>
                                    <TranslatedFileInput
                                      id="webImageAr"
                                      name="webImageAr"
                                      accept="image/jpeg, image/jpg, image/png, image/webp"
                                      invalid={
                                        !!(
                                          touched.webImageAr && errors.webImageAr
                                        )
                                      }
                                      onChange={e =>
                                        handleFileChange(
                                          e,
                                          setFieldValue,
                                          "webImageAr",
                                          webBannerWidth,
                                          webBannerHeight
                                        )
                                      }
                                    />
                                    {compressingFields.webImageAr && (
                                      <Spinner
                                        color="primary"
                                        className="file_upload_btn"
                                      />
                                    )}
                                    <TextError name={"webImageAr"} />
                                    <SlideImagePreview
                                      fileValue={values.webImageAr}
                                      urlValue={values.webImageUrlAr}
                                      imageType={values.webImageUrlTypeAr}
                                      previewWidth={webBannerWidth}
                                      previewHeight={webBannerHeight}
                                      alt={t("Web image preview")}
                                    />
                                  </div>
                                )}
                                <span>
                                  {t("Recommended size: {{dimension}}", {
                                    dimension: `${webBannerWidth}x${webBannerHeight}`,
                                  })}
                                </span>
                              </div>
                            </div>

                            <div className="col-lg-6">
                              <div className="form_field">
                                <label htmlFor="">
                                  {t("Web Image Type(English)")}
                                </label>
                                <Field
                                  as="select"
                                  id="webImageType"
                                  name="webImageType"
                                  onChange={e => {
                                    const selectedValue = e.target.value
                                    setFieldValue("webImageType", selectedValue)
                                    setFieldValue("webImageURL", "")
                                    setFieldValue("webImageFile", null)
                                  }}
                                >
                                  <option value="URL">{t("Image Url")}</option>
                                  <option value="UPLOAD">
                                    {t("Upload Image")}
                                  </option>
                                </Field>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="form_field">
                                {values.webImageType === "URL" && (
                                  <div className="form_field">
                                    <label htmlFor="webImageURL">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Web Image URL(English)")}
                                    </label>
                                    <Field
                                      type="text"
                                      id="webImageURL"
                                      name="webImageURL"
                                      className={`form-control ${
                                        touched.webImageURL &&
                                        errors.webImageURL
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                    />
                                    <TextError name={"webImageURL"} />
                                    <SlideImagePreview
                                      fileValue={values.webImageFile}
                                      urlValue={values.webImageURL}
                                      imageType="URL"
                                      previewWidth={webBannerWidth}
                                      previewHeight={webBannerHeight}
                                      alt={t("Web image preview")}
                                    />
                                  </div>
                                )}
                                {values.webImageType === "UPLOAD" && (
                                  <div className="form_field position-relative">
                                    <label htmlFor="webImageFile">
                                      <span className="text-danger">*</span>{" "}
                                      {t("Upload Web Image(English)")}
                                    </label>
                                    <TranslatedFileInput
                                      id="webImageFileEn"
                                      name="webImageFile"
                                      accept="image/jpeg, image/jpg, image/png, image/webp"
                                      invalid={
                                        !!(
                                          touched.webImageFile &&
                                          errors.webImageFile
                                        )
                                      }
                                      onChange={e =>
                                        handleFileChange(
                                          e,
                                          setFieldValue,
                                          "webImageFile",
                                          webBannerWidth,
                                          webBannerHeight
                                        )
                                      }
                                    />
                                    {compressingFields.webImageFile && (
                                      <Spinner
                                        color="primary"
                                        className="file_upload_btn"
                                      />
                                    )}
                                    <TextError name={"webImageFile"} />
                                    <SlideImagePreview
                                      fileValue={values.webImageFile}
                                      urlValue={values.webImageURL}
                                      imageType={values.webImageType}
                                      previewWidth={webBannerWidth}
                                      previewHeight={webBannerHeight}
                                      alt={t("Web image preview")}
                                    />
                                  </div>
                                )}
                                <span>
                                  {t("Recommended size: {{dimension}}", {
                                    dimension: `${webBannerWidth}x${webBannerHeight}`,
                                  })}
                                </span>
                              </div>
                            </div>
                          </>
                        )}

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="">
                              {t(
                                "Apply the same web images to both language"
                              )}
                            </label>
                            <Switch
                              isChecked={values.applySameWebImageOnBothLang}
                              onClickHandler={() => {
                                setFieldValue(
                                  "applySameWebImageOnBothLang",
                                  !values.applySameWebImageOnBothLang
                                )
                              }}
                            />
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="form_field">
                            <label
                              htmlFor=""
                              className="cursor-pointer"
                              onClick={() =>
                                setFieldValue(
                                  "hasWebAdvancedSettings",
                                  !values.hasWebAdvancedSettings
                                )
                              }
                            >
                              {t("Web Advance Settings")}{" "}
                              <i
                                className={`bx bxs-${
                                  values.hasWebAdvancedSettings ? "up" : "down"
                                }-arrow`}
                              ></i>
                            </label>
                          </div>
                        </div>

                        {values.hasWebAdvancedSettings && (
                          <SlideAdvancedSettings
                            fields={WEB_ADVANCED_FIELDS}
                            values={values}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            canUseGiftCardListLink={canUseGiftCardListLink}
                            giftCardCategories={giftCardCategories}
                            allCategories={allCategories}
                            itemsListDropdown={itemsListDropdown}
                            allItemsList={allItemsList}
                            language={language}
                          />
                        )}

                        <div className="col-lg-12">
                          <div className="d-flex justify-content-end mt-3">
                            <Button
                              type="button"
                              color="secondary"
                              className="mx-2"
                            >
                              <Link to={moduleConfig.basePath} className="text-white ">
                                {t("Cancel")}
                              </Link>
                            </Button>
                            <Button
                              color="success"
                              type="submit"
                              disabled={
                                isLoading || isUpdating || isAnyImageCompressing
                              }
                            >
                              {mode === "add" ? t("Add") : t("Save")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddSlide
